import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Can from 'components/Can';
import { buildUrl } from 'utils';
import { useConfirm } from 'hooks';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import { CollapseCard } from 'components/UI';
import { UpdateUserGroupModal } from 'components/Modals';
import { useHistory, withRouter } from 'react-router-dom';
import { DocumentsActions } from 'state/modules/documents';
import { userGroupsActions } from 'state/modules/userGroups';
import { Box, List, Grid, Button, ListItem, Typography, ListItemText } from '@material-ui/core';
import { useStyles, listTextStyles, customCardStyles, collapseCardStyles } from './Details.styled';

const Details = ({ id }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const [open, setOpen] = useState(false);
  const { orgType } = useContext(UserContext);
  const collapseCardClasses = collapseCardStyles();
  const { file } = useSelector((state) => state.documents);
  // const { localeFormat } = useSelector((state) => state.organizations);

  const { userGroup, fetchedDeleteUserGroupSuccess } = useSelector((state) => state.userGroups);

  const download = () => {
    const zipURL = window.URL.createObjectURL(file);
    const tempLink = document.createElement('a');
    tempLink.href = zipURL;
    tempLink.download = `documents-${Math.floor(Date.now() / 1000)}.zip`;
    tempLink.click();
    dispatch(DocumentsActions.resetFile());
  };

  const [toggleDeleteUserGroup, DeleteUserGroupConfirm] = useConfirm({
    title: i18n.t('user_groups.delete_user_group'),
    message: i18n.t('user_groups.delete_user_group_confirm'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(userGroupsActions.deleteUserGroup(id));
    },
  });

  useEffect(() => {
    if (fetchedDeleteUserGroupSuccess) {
      dispatch(userGroupsActions.resetDeleteUserGroup());
      history.push('/users-groups');
    }
  }, [fetchedDeleteUserGroupSuccess, dispatch, history]);

  useEffect(() => {
    if (id) {
      dispatch(userGroupsActions.fetchUserGroupById(id));
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (file) {
      download();
    }
  }, [file]); // eslint-disable-line

  /**
   * Parse user group data
   * @param {object} user Object with dealer data
   */
  const getUserGroupData = (userGroup = {}) => {
    const ug = (userGroup?.default_vehicle_group || []).map((val) => {
      return {
        label: val?.name,
        value: '',
        id: val?.id,
      };
    });
    const dealerInfo = [
      {
        title: i18n.t('common.basic_info'),
        items: [
          { label: 'Name:', value: `${userGroup?.name || ''}` || '-' },
          {
            label: 'Group Type:',
            value: userGroup?.user_group_type
              ? i18n.t(`group_type.${userGroup?.user_group_type}`) || '-'
              : '-',
          },
          {
            label: 'Default Role:',
            value: userGroup?.role_default
              ? i18n.t(`roles.${userGroup?.role_default}`) || '-'
              : '-',
            classes: 'capitalize',
          },
        ],
      },
      {
        title: i18n.t(orgType === 'msOwnerOrg' ? 'fleets.fleets' : 'user_groups.vehicle_groups'),
        items: ug,
      },
    ];

    return dealerInfo;
  };

  const handleOpenUpdateModal = () => {
    setOpen((prevState) => !prevState);
  };

  /**
   * handle Delete user group modal
   */
  const handleDeleteGroupModal = () => {
    toggleDeleteUserGroup();
  };

  const items = [
    {
      title: i18n.t('user_groups.delete_user_group'),
      onClick: handleDeleteGroupModal,
    },
  ];

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'user',
        type: 'can',
      }}
      title={i18n.t('user_group.widget_title')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
      data-cy="user-group-widget-card"
    >
      <Box className={classes.cardContentPadding}>
        <Grid
          container
          direction="row"
          alignItems="center"
          className={classes.gridContainer}
          spacing={1}
        >
          <Grid item xs={8}>
            <Typography variant="h3">
              <Box fontSize={16} color="#2C2C2E" fontWeight="500">
                ID# {id}
              </Box>
            </Typography>
          </Grid>
          <Can I="update" on="user_group">
            <Grid item xs={4} className={`${classes.textRight}`}>
              <Typography>
                <Button
                  color="primary"
                  onClick={handleOpenUpdateModal}
                  className={classes.userGroupLink}
                >
                  {i18n.t('vehicle.update_info')}
                </Button>
              </Typography>
            </Grid>
          </Can>
        </Grid>

        <div>
          {getUserGroupData(userGroup).map((val, index) => (
            <CollapseCard key={index} title={val.title} customClasses={collapseCardClasses}>
              <List className={classes.userList}>
                {val.items.map((val, index) => (
                  <ListItem key={index} className={classes.userListItem}>
                    <ListItemText
                      classes={listClasses}
                      primary={val.label}
                      onClick={() => {
                        if (val?.id) {
                          if (orgType !== 'msOwnerOrg') {
                            history.push(buildUrl(`/service-groups/${val.id}`));
                          } else {
                            history.push(buildUrl(`/fleets/${val.id}`));
                          }
                        }
                      }}
                      secondary={
                        <Box
                          display="flex"
                          component="span"
                          flexDirection="column"
                          className={classes.textRight}
                        >
                          <Typography
                            variant="body2"
                            component="span"
                            color="textSecondary"
                            className={classes?.[val.classes]}
                          >
                            {val.value}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CollapseCard>
          ))}
        </div>
        <DeleteUserGroupConfirm />
        {open && (
          <UpdateUserGroupModal
            id={id}
            open={open}
            userGroup={userGroup}
            handleClick={handleOpenUpdateModal}
          />
        )}
      </Box>
    </Card>
  );
};

export default withRouter(Details);
